import { createContext, useContext, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface NavigationContextType {
  sectionColor: string;
  sectionBgColor: string;
  sectionName: string;
}

const NavigationContext = createContext<NavigationContextType>({
  sectionColor: 'text-cyan-300',
  sectionBgColor: 'bg-cyan-500/20',
  sectionName: 'Capture',
});

export const useNavigation = () => useContext(NavigationContext);

interface NavigationProviderProps {
  children: ReactNode;
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  const location = useLocation();

  // Determine active section based on current path
  const getSectionInfo = (): NavigationContextType => {
    const currentPath = location.pathname;
    
    // Assess section (check BEFORE Capture to catch /capacity-skills/intelligence)
    if (currentPath.startsWith('/assessments') || 
        currentPath.startsWith('/scenarios') ||
        currentPath.startsWith('/capacity-skills/intelligence') ||
        currentPath.startsWith('/integrations')) {
      return {
        sectionColor: 'text-blue-300',
        sectionBgColor: 'bg-blue-500/20',
        sectionName: 'Assess',
      };
    }
    
    // Capture section
    if (currentPath.startsWith('/capacity-skills') || 
        currentPath.startsWith('/business-forecast') || 
        currentPath.startsWith('/processes') || 
        currentPath.startsWith('/customers') || 
        currentPath.startsWith('/suppliers') || 
        currentPath.startsWith('/risks')) {
      return {
        sectionColor: 'text-cyan-300',
        sectionBgColor: 'bg-cyan-500/20',
        sectionName: 'Capture',
      };
    }
    
    // Define Appetite section
    if (currentPath.startsWith('/policies') || 
        currentPath.startsWith('/risk-appetite')) {
      return {
        sectionColor: 'text-purple-300',
        sectionBgColor: 'bg-purple-500/20',
        sectionName: 'Define Appetite',
      };
    }
    
    // Mitigate section
    if (currentPath.startsWith('/controls') || 
        currentPath.startsWith('/compliance') || 
        currentPath.startsWith('/actions') || 
        currentPath.startsWith('/playbooks') || 
        currentPath.startsWith('/bcp') || 
        currentPath.startsWith('/kpis') || 
        currentPath.startsWith('/alerts')) {
      return {
        sectionColor: 'text-green-300',
        sectionBgColor: 'bg-green-500/20',
        sectionName: 'Mitigate',
      };
    }
    
    // Administration section
    if (currentPath.startsWith('/notifications') || 
        currentPath.startsWith('/reports') || 
        currentPath.startsWith('/audit') || 
        currentPath.startsWith('/settings')) {
      return {
        sectionColor: 'text-neutral-300',
        sectionBgColor: 'bg-neutral-500/20',
        sectionName: 'Administration',
      };
    }
    
    // Default to Capture
    return {
      sectionColor: 'text-cyan-300',
      sectionBgColor: 'bg-cyan-500/20',
      sectionName: 'Capture',
    };
  };

  const sectionInfo = getSectionInfo();

  return (
    <NavigationContext.Provider value={sectionInfo}>
      {children}
    </NavigationContext.Provider>
  );
}
